import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import BirthTimePicker from "../components/BirthTimePicker";
import { TIMEZONES, guessTimezoneOffset } from "../lib/timezones";
import { authFetch, useAuth } from "../context/AuthContext";
import { toast } from "sonner";

export default function Onboarding() {
    const navigate = useNavigate();
    const { user, refresh } = useAuth();
    const isEdit = !!user?.has_birth_info;
    const [step, setStep] = useState(1);
    const [form, setForm] = useState({
        birth_date: user?.birth_date || "",
        birth_time: user?.birth_time || "12:00",
        birth_location: user?.birth_location || "",
        latitude: user?.latitude || 0,
        longitude: user?.longitude || 0,
        tz_offset: user?.tz_offset || guessTimezoneOffset(),
        gender: user?.gender || "",
        interested_in: user?.interested_in || "",
        bio: user?.bio || "",
    });
    const [loading, setLoading] = useState(false);
    const [searchingLoc, setSearchingLoc] = useState(false);

    const update = (k, v) => setForm({ ...form, [k]: v });

    // Use OpenStreetMap Nominatim for city lookup (no API key needed)
    const findLocation = async () => {
        if (!form.birth_location.trim()) return;
        setSearchingLoc(true);
        try {
            const res = await fetch(
                `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(form.birth_location)}&format=json&limit=1`
            );
            const data = await res.json();
            if (data && data[0]) {
                setForm((f) => ({
                    ...f,
                    latitude: parseFloat(data[0].lat),
                    longitude: parseFloat(data[0].lon),
                    birth_location: data[0].display_name,
                }));
                toast.success("Location found in the celestial map.");
            } else {
                toast.error("Location not found. Try another city.");
            }
        } catch {
            toast.error("Location lookup failed.");
        } finally {
            setSearchingLoc(false);
        }
    };

    const onSubmit = async () => {
        if (!form.birth_date || !form.birth_time || !form.birth_location || (form.latitude === 0 && form.longitude === 0)) {
            toast.error("All birth details are required, including location coordinates.");
            return;
        }
        setLoading(true);
        try {
            await authFetch("post", "/profile/birth-info", form);
            await refresh();
            toast.success(isEdit ? "Chart re-cast with new data." : "Your chart has been cast.");
            navigate("/dashboard");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to save");
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Navbar />
            <main className="min-h-screen flex items-center justify-center px-6 pt-24 pb-12">
                <div className="glass-gold p-10 max-w-2xl w-full fade-in">
                    <p className="label-tracking text-gold text-center">{isEdit ? "Recast your chart" : `Step ${step} of 3`}</p>
                    <h1 className="font-serif text-4xl text-center mt-3">
                        {step === 1 && (isEdit ? "Update your moment" : "Your moment of arrival")}
                        {step === 2 && "Where the sky was above you"}
                        {step === 3 && "About you"}
                    </h1>
                    <p className="text-center text-muted text-sm mt-3 max-w-md mx-auto">
                        {step === 1 && (isEdit
                            ? "Correct any details and your Sun, Moon, and Rising will be recomputed."
                            : "The exact date and time you took your first breath shapes the sky above you forever.")}
                        {step === 2 && "The location of your birth determines the angle of the heavens."}
                        {step === 3 && "Tell us how you love and who you seek."}
                    </p>

                    {step === 1 && (
                        <div className="mt-10 space-y-6">
                            <div>
                                <label className="label-tracking">Birth Date</label>
                                <input
                                    type="date" required value={form.birth_date}
                                    onChange={(e) => update("birth_date", e.target.value)}
                                    className="w-full mt-2 px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-gold outline-none"
                                    data-testid="onboard-birthdate"
                                />
                            </div>
                            <div>
                                <label className="label-tracking">Birth Time</label>
                                <div className="mt-2">
                                    <BirthTimePicker
                                        value={form.birth_time}
                                        onChange={(v) => update("birth_time", v)}
                                        testIdPrefix="onboard-birthtime"
                                    />
                                </div>
                                <p className="text-xs text-muted mt-3">
                                    Birth time is crucial — your Rising sign rotates every ~2 hours, and the Moon shifts daily.
                                    If you're unsure, use 12:00 PM (noon) and only your Sun sign will be exact.
                                </p>
                            </div>
                            <div>
                                <label className="label-tracking">Timezone at Birth</label>
                                <select
                                    value={form.tz_offset}
                                    onChange={(e) => update("tz_offset", e.target.value)}
                                    className="w-full mt-2 px-4 py-3 rounded-xl bg-[#0A0A1A] border border-white/10 focus:border-gold outline-none"
                                    data-testid="onboard-tzoffset"
                                >
                                    {TIMEZONES.map((tz) => (
                                        <option key={tz.value} value={tz.value}>{tz.label}</option>
                                    ))}
                                </select>
                                <p className="text-xs text-muted mt-2">
                                    Use the UTC offset that was in effect at your birth location and date (account for daylight saving time).
                                </p>
                            </div>
                            <button
                                onClick={() => form.birth_date && setStep(2)}
                                disabled={!form.birth_date}
                                className="btn-gold-solid w-full"
                                data-testid="onboard-step1-next"
                            >Continue</button>
                        </div>
                    )}

                    {step === 2 && (
                        <div className="mt-10 space-y-6">
                            <div>
                                <label className="label-tracking">Birth Place</label>
                                <div className="flex flex-col sm:flex-row sm:gap-2 gap-3 mt-2">
                                    <input
                                        type="text" required
                                        placeholder="e.g., Mount Sinai Hospital, NYC"
                                        value={form.birth_location}
                                        onChange={(e) => update("birth_location", e.target.value)}
                                        className="min-w-0 flex-1 w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-gold outline-none"
                                        data-testid="onboard-location"
                                    />
                                    <button
                                        onClick={findLocation}
                                        disabled={searchingLoc}
                                        className="btn-gold whitespace-nowrap w-full sm:w-auto flex-shrink-0"
                                        data-testid="onboard-find-location"
                                    >
                                        {searchingLoc ? "..." : "Locate"}
                                    </button>
                                </div>
                                <p className="text-xs text-muted mt-2">
                                    Use the hospital name or street address — Rising sign accuracy depends on precise coordinates.
                                </p>
                                {form.latitude !== 0 && (
                                    <p className="text-xs text-gold mt-2" data-testid="onboard-coords">
                                        ✦ {form.latitude.toFixed(4)}°, {form.longitude.toFixed(4)}°
                                    </p>
                                )}
                            </div>
                            <div className="flex gap-3">
                                <button onClick={() => setStep(1)} className="btn-gold flex-1" data-testid="onboard-step2-back">Back</button>
                                <button
                                    onClick={() => form.latitude !== 0 && setStep(3)}
                                    disabled={form.latitude === 0}
                                    className="btn-gold-solid flex-1"
                                    data-testid="onboard-step2-next"
                                >Continue</button>
                            </div>
                        </div>
                    )}

                    {step === 3 && (
                        <div className="mt-10 space-y-6">
                            <div>
                                <label className="label-tracking">I am</label>
                                <select
                                    value={form.gender}
                                    onChange={(e) => update("gender", e.target.value)}
                                    className="w-full mt-2 px-4 py-3 rounded-xl bg-[#0A0A1A] border border-white/10 focus:border-gold outline-none"
                                    data-testid="onboard-gender"
                                >
                                    <option value="">Select...</option>
                                    <option value="woman">A woman</option>
                                    <option value="man">A man</option>
                                    <option value="nonbinary">Non-binary</option>
                                </select>
                            </div>
                            <div>
                                <label className="label-tracking">Interested in</label>
                                <select
                                    value={form.interested_in}
                                    onChange={(e) => update("interested_in", e.target.value)}
                                    className="w-full mt-2 px-4 py-3 rounded-xl bg-[#0A0A1A] border border-white/10 focus:border-gold outline-none"
                                    data-testid="onboard-interested"
                                >
                                    <option value="">Select...</option>
                                    <option value="woman">Women</option>
                                    <option value="man">Men</option>
                                    <option value="all">Everyone</option>
                                </select>
                            </div>
                            <div>
                                <label className="label-tracking">A few words about you</label>
                                <textarea
                                    rows={3}
                                    placeholder="What kindles your soul?"
                                    value={form.bio}
                                    onChange={(e) => update("bio", e.target.value)}
                                    className="w-full mt-2 px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-gold outline-none"
                                    data-testid="onboard-bio"
                                />
                            </div>
                            <div className="flex gap-3">
                                <button onClick={() => setStep(2)} className="btn-gold flex-1" data-testid="onboard-step3-back">Back</button>
                                <button
                                    onClick={onSubmit}
                                    disabled={loading}
                                    className="btn-gold-solid flex-1"
                                    data-testid="onboard-submit"
                                >
                                    {loading ? "Casting Chart..." : (isEdit ? "Recast My Chart" : "Cast My Chart")}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </main>
        </>
    );
}
